import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Chess,
  ChessGame as ChessEngine,
  createTestEnPassantBoard,
  createTestPromotionBoard,
  positionToSquare,
  type Color,
  type PieceSymbol,
  type Square,
} from '@skillarea/game-chess';

import type { IChessGameResult } from './types';

export interface IUseChessLocalGameOptions {
  turnDurationMs: number;
  enableAI: boolean;
  aiDifficulty?: number;
  onGameEnd?: (result: IChessGameResult) => void;
  onSurrender?: () => void;
}

export interface IUseChessLocalGameReturn {
  fen: string;
  currentTurn: Color;
  whiteTimeMs: number;
  blackTimeMs: number;
  isGameOver: boolean;
  isBotThinking: boolean;
  winner: Color | null;
  isDraw: boolean;
  handleMove: (
    from: { row: number; col: number },
    to: { row: number; col: number },
    promotion?: 'Q' | 'R' | 'B' | 'N',
  ) => void;
  handleSurrender: () => void;
  loadTestPromotionPosition: () => void;
  loadTestEnPassantPosition: () => void;
}

interface IChessBotAction {
  from: string;
  to: string;
  promotion?: string;
}

async function fetchBotMove(
  fen: string,
  difficulty01: number,
): Promise<IChessBotAction> {
  const response = await window.fetch('/api/game/bot-move', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      gameSlug: 'classic-chess',
      state: { fen },
      playerSlot: 1,
      difficulty01,
    }),
  });

  if (!response.ok) {
    throw new Error(`Bot move request failed: ${response.status}`);
  }

  const data = (await response.json()) as { action: IChessBotAction };
  return data.action;
}

export function useChessLocalGame({
  aiDifficulty,
  enableAI,
  onGameEnd,
  onSurrender,
  turnDurationMs,
}: IUseChessLocalGameOptions): IUseChessLocalGameReturn {
  const engineRef = useRef<ChessEngine | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentTurnRef = useRef<Color>('w');
  const botRequestId = useRef(0);

  const [fen, setFen] = useState(() => new Chess().fen());
  const [currentTurn, setCurrentTurn] = useState<Color>('w');
  const [whiteTimeMs, setWhiteTimeMs] = useState(turnDurationMs);
  const [blackTimeMs, setBlackTimeMs] = useState(turnDurationMs);
  const [isGameOver, setIsGameOver] = useState(false);
  const [isBotThinking, setIsBotThinking] = useState(false);
  const [winner, setWinner] = useState<Color | null>(null);
  const [isDraw, setIsDraw] = useState(false);

  currentTurnRef.current = currentTurn;

  const syncState = useCallback(() => {
    if (!engineRef.current) return;
    const state = engineRef.current.getState();
    setFen(engineRef.current.getFen());
    setCurrentTurn(state.currentTurn);
    setWhiteTimeMs(state.whiteTimeMs);
    setBlackTimeMs(state.blackTimeMs);
    if (state.status !== 'playing' && state.status !== 'idle') {
      setIsGameOver(true);
      setWinner(state.winner ?? null);
      setIsDraw(state.status === 'draw' || state.status === 'stalemate');
      onGameEnd?.({
        winner: state.winner ?? null,
        isDraw: state.status === 'draw' || state.status === 'stalemate',
        reason: state.status,
      });
    }
  }, [onGameEnd]);

  useEffect(() => {
    engineRef.current = new ChessEngine(turnDurationMs);
    engineRef.current.start();
    setFen(engineRef.current.getFen());
    const state = engineRef.current.getState();
    setCurrentTurn(state.currentTurn);
    setWhiteTimeMs(state.whiteTimeMs);
    setBlackTimeMs(state.blackTimeMs);
    setIsBotThinking(false);
    botRequestId.current++;
  }, [aiDifficulty, enableAI, turnDurationMs]);

  useEffect(() => {
    if (isGameOver) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    timerRef.current = setInterval(() => {
      if (currentTurnRef.current === 'w') {
        setWhiteTimeMs((prev) => Math.max(0, prev - 1000));
      } else {
        setBlackTimeMs((prev) => Math.max(0, prev - 1000));
      }
    }, 1000);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isGameOver]);

  useEffect(() => {
    if (!engineRef.current || isGameOver) return;
    if (currentTurn === 'w') {
      engineRef.current.setTimeLeft('w', whiteTimeMs);
      if (whiteTimeMs <= 0) {
        setIsGameOver(true);
        setWinner('b');
        onGameEnd?.({ winner: 'b', isDraw: false, reason: 'timeout' });
      }
    } else {
      engineRef.current.setTimeLeft('b', blackTimeMs);
      if (blackTimeMs <= 0) {
        setIsGameOver(true);
        setWinner('w');
        onGameEnd?.({ winner: 'w', isDraw: false, reason: 'timeout' });
      }
    }
  }, [whiteTimeMs, blackTimeMs, currentTurn, isGameOver, onGameEnd]);

  const requestBotMove = useCallback(
    async (currentFen: string) => {
      const requestId = ++botRequestId.current;
      setIsBotThinking(true);

      try {
        const botMove = await fetchBotMove(currentFen, aiDifficulty ?? 0.5);

        if (requestId !== botRequestId.current || !engineRef.current) {
          return;
        }

        const result = engineRef.current.applyOpponentMove(
          botMove.from as Square,
          botMove.to as Square,
          botMove.promotion as PieceSymbol | undefined,
        );

        if (result.success) {
          syncState();
        }
      } catch (error) {
        console.error('[ChessLocalGame] Bot move failed:', error);
      } finally {
        if (requestId === botRequestId.current) {
          setIsBotThinking(false);
        }
      }
    },
    [aiDifficulty, syncState],
  );

  const handleMove = useCallback(
    (
      from: { row: number; col: number },
      to: { row: number; col: number },
      promotion?: 'Q' | 'R' | 'B' | 'N',
    ) => {
      if (!engineRef.current) return;
      const fromSquare = positionToSquare(from.row, from.col);
      const toSquare = positionToSquare(to.row, to.col);
      const promotionLower = promotion?.toLowerCase() as
        | 'q'
        | 'r'
        | 'b'
        | 'n'
        | undefined;
      const result = engineRef.current.makeMove(
        fromSquare,
        toSquare,
        promotionLower,
      );
      if (result.success) {
        syncState();
        if (!result.isGameOver && enableAI) {
          const currentFen = engineRef.current.getFen();
          void requestBotMove(currentFen);
        }
      }
    },
    [syncState, enableAI, requestBotMove],
  );

  const handleSurrender = useCallback(() => {
    setIsGameOver(true);
    setWinner(currentTurn === 'w' ? 'b' : 'w');
    onSurrender?.();
  }, [onSurrender, currentTurn]);

  const loadTestPromotionPosition = useCallback(() => {
    if (!engineRef.current) return;
    engineRef.current.loadPosition(createTestPromotionBoard());
    setIsGameOver(false);
    setWinner(null);
    syncState();
  }, [syncState]);

  const loadTestEnPassantPosition = useCallback(() => {
    if (!engineRef.current) return;
    engineRef.current.loadPosition(createTestEnPassantBoard(), {
      enPassantTarget: 'd6',
      lastMove: { from: 'd7', to: 'd5' },
      moveCount: 1,
    });
    setIsGameOver(false);
    setWinner(null);
    syncState();
  }, [syncState]);

  return {
    fen,
    currentTurn,
    whiteTimeMs,
    blackTimeMs,
    isGameOver,
    isBotThinking,
    winner,
    isDraw,
    handleMove,
    handleSurrender,
    loadTestPromotionPosition,
    loadTestEnPassantPosition,
  };
}
